import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { BaseComponent } from '@core/base.component';
import { TestHooks } from './sign-up.hooks';
import { Logger } from '@core/logger';
import { SnackbarService } from '@services/ui/snackbar.service';
import { filter, switchMap, take } from 'rxjs/operators';
import { BusinessSignUpRequestService } from '@services/closer-api/businessSignUpRequests.service';
import { Store } from '@ngxs/store';
import { AuthState } from '@state/auth';
import {
	BusinessSignUpRequestPayload,
	BusinessSignupRequestStatutes,
} from '@models/businessSignUpRequests';
import { BusinessesSignUpRequestsRepo } from '@repos/signupRequests/signUpRequests.repo';
import { UrlResult, UrlService } from '@services/url/url.service';
import { Address } from '@models/address';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';

interface SignUpVM {
	loading: boolean;
	submitted: boolean;
	requestSent: boolean;
}

enum SIGNUP_FORM_FIELDS {
	EMAIL = 'email',
	FIRST_NAME = 'firstName',
	LAST_NAME = 'lastName',
	COMPANY_NAME = 'companyName',
	ADDRESS = 'address',
	PHONE_NUMBER = 'phoneNumber',
	UID = 'uid',
}

@Component({
	selector: 'app-sign-up',
	templateUrl: './sign-up.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class SignUpComponent extends BaseComponent<SignUpVM> implements OnInit {
	@SelectSnapshot(AuthState.getSignUpRequest)
	signupRequest: BusinessSignUpRequestPayload;
	public SIGNUP_FORM_FIELDS = SIGNUP_FORM_FIELDS;
	public testHooks = TestHooks;

	get loginUrl(): UrlResult {
		return UrlService.Login();
	}

	constructor(
		private fb: FormBuilder,
		private businessSignUpRequestService: BusinessSignUpRequestService,
		private snackbarService: SnackbarService,
		private store: Store,
		private businessesSignUpRequestsRepo: BusinessesSignUpRequestsRepo,
	) {
		super({
			loading: false,
			submitted: false,
			requestSent: false,
		});
	}
	ngOnInit(): void {
		const loggedInUser = this.store.select(AuthState.getLoggedInUser).pipe(
			filter((user) => !!user),
			take(1),
		);

		loggedInUser.subscribe((user) => {
			this.authForm.controls[SIGNUP_FORM_FIELDS.EMAIL].setValue(user.email);
			this.authForm.controls[SIGNUP_FORM_FIELDS.EMAIL].disable();
			this.authForm.controls[SIGNUP_FORM_FIELDS.UID].setValue(user.uid);
		});

		loggedInUser
			.pipe(
				switchMap((user) =>
					this.businessesSignUpRequestsRepo.CountByEmailAndStatus(
						user.email,
						BusinessSignupRequestStatutes.PENDING,
					),
				),
				filter((count) => count > 0),
				take(1),
			)
			.subscribe(() => {
				this.VM = { requestSent: true };
			});
	}

	authForm = this.fb.group<{
		[SIGNUP_FORM_FIELDS.FIRST_NAME]: FormControl<string | null>;
		[SIGNUP_FORM_FIELDS.LAST_NAME]: FormControl<string | null>;
		[SIGNUP_FORM_FIELDS.UID]: FormControl<string | null>;
		[SIGNUP_FORM_FIELDS.PHONE_NUMBER]: FormControl<string | null>;
		[SIGNUP_FORM_FIELDS.ADDRESS]: FormControl<Address | null>;
		[SIGNUP_FORM_FIELDS.COMPANY_NAME]: FormControl<string | null>;
		[SIGNUP_FORM_FIELDS.EMAIL]: FormControl<string | null>;
	}>({
		[SIGNUP_FORM_FIELDS.FIRST_NAME]: this.fb.control('', [Validators.required]),
		[SIGNUP_FORM_FIELDS.LAST_NAME]: this.fb.control('', [Validators.required]),
		[SIGNUP_FORM_FIELDS.EMAIL]: this.fb.control('', [
			Validators.required,
			Validators.email,
		]),
		[SIGNUP_FORM_FIELDS.COMPANY_NAME]: this.fb.control('', [
			Validators.required,
		]),
		[SIGNUP_FORM_FIELDS.PHONE_NUMBER]: this.fb.control('', [
			Validators.required,
		]),
		[SIGNUP_FORM_FIELDS.ADDRESS]: this.fb.control(
			{
				street: '',
				unit: '',
				city: '',
				postcode: '',
				province: '',
				country: 'Canada',
				countryCode: 'CA',
			},
			[Validators.required],
		),
		[SIGNUP_FORM_FIELDS.UID]: this.fb.control({ value: '', disabled: true }),
	});

	public setAddress(address: Address) {
		this.authForm.patchValue({
			[SIGNUP_FORM_FIELDS.ADDRESS]: address,
		});
	}

	async submit(): Promise<void> {
		this.VM = { submitted: true };

		if (this.authForm.valid) {
			this.VM = { loading: true };

			try {
				const request: BusinessSignUpRequestPayload =
					this.authForm.getRawValue();

				await this.businessSignUpRequestService
					.create(request)
					.pipe(take(1))
					.toPromise();

				this.VM = { requestSent: true, loading: false };
			} catch (error) {
				this.VM = { loading: false };
				Logger.error(error);
				this.snackbarService.error(
					'Unable to submit request. Please try again.',
				);
			}
		}
	}
}
